import {
  configureStore,
  createListenerMiddleware,
  isAnyOf,
} from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import { CART_LOCAL_STORAGE_KEY } from '../services/viva/constants';

import * as slices from './slices';
import { initialCheckoutState } from './slices/checkout';
import {
  getObjectForLocalStorageKey,
  removeObjectForLocalStorageKey,
  setObjectForLocalStorageKey,
} from './utils';
import {
  addOnUpdateDTO,
  removeByTypeAndDateTimeDTO,
  clearDateFromCheckoutDTO,
  setFlowSelectionDTO,
  removeAddOnDTO,
  setAttributesDTO,
} from './utils/checkout';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { CheckoutCart, CheckoutType } from './types';

const mutuallyExclusiveMiddleware = createListenerMiddleware();

const getLocalStorageCheckout = () => {
  const localStorageCart = getObjectForLocalStorageKey<CheckoutType>(
    CART_LOCAL_STORAGE_KEY,
  );

  if (!localStorageCart) return initialCheckoutState;

  return localStorageCart;
};

// Order initialization
mutuallyExclusiveMiddleware.startListening({
  matcher: isAnyOf(slices.orderSlice.actions.initialize),
  effect: (action: PayloadAction<{ flow?: unknown }>, { dispatch }) => {
    if (action.payload.flow === null) {
      return;
    }
  },
});

// Adding to cart
mutuallyExclusiveMiddleware.startListening({
  actionCreator: slices.checkoutSlice.actions.setFlowSelection,
  effect: (action) => {
    if (!action.payload.data) {
      return;
    }

    const localStorageCart = getObjectForLocalStorageKey<CheckoutType>(
      CART_LOCAL_STORAGE_KEY,
    );
    const cartData = localStorageCart ?? { ...initialCheckoutState };

    const newCartData = setFlowSelectionDTO({
      ...action.payload,
      state: cartData,
    });

    if (!newCartData) return;

    setObjectForLocalStorageKey<CheckoutType>(
      CART_LOCAL_STORAGE_KEY,
      newCartData,
    );
  },
});

// Cart addons
mutuallyExclusiveMiddleware.startListening({
  actionCreator: slices.checkoutSlice.actions.setAddOns,
  effect: (action) => {
    if (!action.payload) {
      return;
    }

    const localStorageCart = getLocalStorageCheckout();

    const cartData = (localStorageCart?.cart as CheckoutCart) ?? {
      ...initialCheckoutState,
    };

    const newAddOnData = addOnUpdateDTO({
      ...action.payload,
      data: cartData.addOnSelections,
    });

    setObjectForLocalStorageKey<CheckoutType>(CART_LOCAL_STORAGE_KEY, {
      ...localStorageCart,
      cart: {
        ...cartData,
        addOnSelections: newAddOnData,
      },
    });
  },
});

// Remove from checkout cart
mutuallyExclusiveMiddleware.startListening({
  actionCreator:
    slices.checkoutSlice.actions.removeItemFromCheckoutCartByTypeAndDateTime,
  effect: (action) => {
    const localStorageCheckout = getLocalStorageCheckout();
    if (!localStorageCheckout) return;

    const newLocalStorageCart = removeByTypeAndDateTimeDTO({
      ...action.payload,
      state: localStorageCheckout,
    });

    setObjectForLocalStorageKey<CheckoutType>(
      CART_LOCAL_STORAGE_KEY,
      newLocalStorageCart,
    );
  },
});

// Remove flow from checkout cart
mutuallyExclusiveMiddleware.startListening({
  actionCreator: slices.checkoutSlice.actions.clearDateFromCheckout,
  effect: (action) => {
    const localStorageCheckout = getLocalStorageCheckout();
    const { flow, dateTime } = action.payload;

    if (!localStorageCheckout) return;

    const newCartData = clearDateFromCheckoutDTO({
      flow,
      dateTime,
      state: localStorageCheckout,
    });

    setObjectForLocalStorageKey<CheckoutType>(
      CART_LOCAL_STORAGE_KEY,
      newCartData,
    );
  },
});

// Checkout page visited, totals calculated
mutuallyExclusiveMiddleware.startListening({
  actionCreator: slices.checkoutSlice.actions.setCheckoutTotals,
  effect: (action) => {
    const localStorageCheckout = getLocalStorageCheckout();

    if (!localStorageCheckout) return;

    const newLocalStorageCart: CheckoutType = {
      ...localStorageCheckout,
      cart: {
        ...localStorageCheckout.cart,
        ...action.payload,
      },
    };

    setObjectForLocalStorageKey<CheckoutType>(
      CART_LOCAL_STORAGE_KEY,
      newLocalStorageCart,
    );
  },
});

// Wizard is initialized
mutuallyExclusiveMiddleware.startListening({
  actionCreator: slices.wizardSlice.actions.initialize,
  effect: (action) => {
    // Clear the local storage cart if no timer
    const localStorageCheckout = getLocalStorageCheckout();

    if (!localStorageCheckout) return;

    if (!localStorageCheckout.counterEndTime && !!localStorageCheckout.cart) {
      setObjectForLocalStorageKey<CheckoutType>(
        CART_LOCAL_STORAGE_KEY,
        initialCheckoutState,
      );
    }
  },
});

// Counter is reset
mutuallyExclusiveMiddleware.startListening({
  actionCreator: slices.wizardSlice.actions.clearCounter,
  effect: () => {
    // If we're removing the counter, then there shouldn't be anything in the cart anymore
    const localStorageCheckout = getLocalStorageCheckout();
    if (!localStorageCheckout) return;

    removeObjectForLocalStorageKey(CART_LOCAL_STORAGE_KEY);
  },
});

// Remove add on from cart
mutuallyExclusiveMiddleware.startListening({
  actionCreator:
    slices.checkoutSlice.actions.removeAddOnItemFromCheckoutByFlowAndDateTime,
  effect: (action) => {
    const localStorageCheckout = getLocalStorageCheckout();
    if (!localStorageCheckout) return;

    const newLocalStorageCart: CheckoutType = removeAddOnDTO({
      ...action.payload,
      state: localStorageCheckout,
    });

    setObjectForLocalStorageKey<CheckoutType>(
      CART_LOCAL_STORAGE_KEY,
      newLocalStorageCart,
    );
  },
});

// Updating attributes
mutuallyExclusiveMiddleware.startListening({
  actionCreator: slices.checkoutSlice.actions.setAttributes,
  effect: (action) => {
    const localStorageCheckout = getLocalStorageCheckout();

    if (!localStorageCheckout) return;

    const newLocalStorageCart: CheckoutType = setAttributesDTO({
      attributes: action.payload,
      state: localStorageCheckout,
    });

    setObjectForLocalStorageKey<CheckoutType>(
      CART_LOCAL_STORAGE_KEY,
      newLocalStorageCart,
    );
  },
});

export const store = configureStore({
  devTools: {
    name: '@tishman/rockefellercenter',
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().prepend(mutuallyExclusiveMiddleware.middleware),
  reducer: {
    [slices.checkoutSlice.name]: slices.checkoutSlice.reducer,
    [slices.wizardSlice.name]: slices.wizardSlice.reducer,
    [slices.orderSlice.name]: slices.orderSlice.reducer,
    // promoCode: slices.promoSlice.reducer,
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export * as actions from './actions';
export * from './selectors';
